import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import {
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {
  selectAllPnlHistory,
  selectGunbotAllAnalysis,
  selectBots,
} from 'src/store/bots'

export default function AIAssistantBar() {
  const [size, setSize] = useState({ width: 500, height: 500 })
  const resizingRef = useRef(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('aiChatMemory')
    return saved ? JSON.parse(saved) : []
  })
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const scrollRef = useRef(null)
  const shouldAutoScroll = useRef(true)

  const pnlHistory = useSelector(selectAllPnlHistory)
  const analysis = useSelector(selectGunbotAllAnalysis)
  const bots = useSelector(selectBots)
  const coremems = useSelector((s) => s.bots.coremems)

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  }

  useEffect(() => {
    if (shouldAutoScroll.current) scrollToBottom()
  }, [messages, open])

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60
    shouldAutoScroll.current = nearBottom
  }

  useEffect(() => {
    localStorage.setItem('aiChatMemory', JSON.stringify(messages.slice(-10)))
  }, [messages])

  const clearChat = () => {
    localStorage.removeItem('aiChatMemory')
    setMessages([])
  }

  const sendMessage = async () => {
    if (!input.trim() || loading) return
    const userMessage = { role: 'user', content: input.trim() }
    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setLoading(true)

    const state = {
      bots: {
        pnlHistory,
        coremems,
        analysis,
        summary: bots.map((b) => ({
          id: b.id,
          name: b.name,
          exchange: b.exchange,
          pairCount: b.pairCount,
          alive: b.alive,
          version: b.version,
        })),
      },
    }

    try {
      const res = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: userMessage.content, state }),
      })
      const data = await res.json()
      const aiMessage = {
        role: 'assistant',
        content: data.answer || data.error || 'No response received',
      }
      setMessages((prev) => [...prev, aiMessage])
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `⚠️ An error occurred: ${err.message}` },
      ])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }
  const startResize = (e) => {
    e.preventDefault()
    e.stopPropagation()
    resizingRef.current = true
    document.body.style.cursor = 'nw-resize'
  }
  
  const stopResize = () => {
    resizingRef.current = false
    document.body.style.cursor = 'default'
  }
  
  const handleResize = (e) => {
    if (!resizingRef.current) return
    
    // Calculate new dimensions based on distance from the Right and Bottom screen edges
    const newWidth = window.innerWidth - e.clientX - 24
    const newHeight = window.innerHeight - e.clientY - 24

    setSize({
      width: Math.max(350, newWidth),
      height: Math.max(350, newHeight),
    })
  }

  useEffect(() => {
    window.addEventListener('mousemove', handleResize)
    window.addEventListener('mouseup', stopResize)
    return () => {
      window.removeEventListener('mousemove', handleResize)
      window.removeEventListener('mouseup', stopResize)
    }
  }, [])
  
  if (!open) {
    return (
      <motion.button
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className='fixed bottom-6 right-6 z-50 rounded-full bg-gray-800 p-3 border border-gray-700 shadow-lg hover:shadow-blue-500/20 transition-all'
        title='Ask AI Assistant'
      >
        <SparklesIcon className='w-6 h-6 text-blue-400' />
      </motion.button>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 40, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className='fixed bottom-6 right-6 z-50 flex flex-col rounded-xl border border-gray-700/50 bg-[#111827]/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden'
      style={{
        width: size.width,
        height: size.height,
        maxWidth: 'calc(100vw - 48px)',
        maxHeight: 'calc(100vh - 48px)'
      }}
    >
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-3 flex-shrink-0 pl-8 bg-gray-900/50 border-b border-gray-800'>
        <div className='flex items-center gap-3'>
          <SparklesIcon className='w-5 h-5 text-blue-500' />
          <span className='font-medium text-md text-gray-200'>
            AI Assistant
          </span>
        </div>
        <div className='flex gap-2 items-center'>
          <button
            onClick={clearChat}
            className='text-gray-500 hover:text-red-400 transition-colors p-1 rounded-full'
            title='Clear chat'
          >
            <TrashIcon className='w-5 h-5' />
          </button>
          <button
            onClick={() => setOpen(false)}
            className='text-gray-400 hover:text-white transition-colors p-1 rounded-full'
            title='Close'
          >
            <XMarkIcon className='w-5 h-5' />
          </button>
        </div>
      </div>

      {/* Chat */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className='flex-1 overflow-y-auto px-4 pb-4 space-y-5 text-sm scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent'
      >
        {messages.length === 0 && (
          <div className='text-gray-500 text-center mt-12 text-sm'>
            Ask about your PnL, ROI, or risk...
          </div>
        )}

        <AnimatePresence>
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={clsx('flex items-start gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            > 
               {msg.role === 'assistant' && <div className='w-6 h-6 rounded-full bg-gray-800 border border-gray-700 flex-shrink-0 mt-1 flex items-center justify-center'><SparklesIcon className="w-3 h-3 text-blue-500"/></div>}
              <div
                className={clsx(
                  'max-w-[85%] px-4 py-2.5 rounded-lg leading-relaxed whitespace-pre-wrap',
                  msg.role === 'user'
                    ? 'bg-blue-900/30 text-blue-100 border border-blue-800/30 rounded-br-none'
                    : 'bg-gray-800/40 text-gray-300 border border-gray-700/50 rounded-bl-none'
                )}
              >
                 <strong className={clsx('block mb-1 text-xs', msg.role === 'user' ? 'text-blue-400' : 'text-gray-500')}>
                  {msg.role === 'user' ? 'You' : 'AI'}
                </strong> 
                {msg.content}
              </div>
            </motion.div>
          ))}

          {loading && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className='flex items-start gap-3'
            >
              <div className='w-6 h-6 rounded-full bg-gray-800 border border-gray-700 flex-shrink-0 mt-1'></div>
              <div className='bg-gray-800/40 border border-gray-700/50 px-4 py-2.5 rounded-lg rounded-bl-none flex items-center gap-2'>
                <span className='inline-flex items-center gap-1.5'>
                  <span className='w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.2s]'></span>
                  <span className='w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.1s]'></span>
                  <span className='w-2 h-2 bg-gray-500 rounded-full animate-bounce'></span>
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Input */}
      <div className='flex items-center border-t border-gray-800 bg-[#0f131a] px-3 py-2.5'>
        <textarea
          rows={1}
          className='flex-1 resize-none bg-transparent text-sm text-gray-200 outline-none placeholder-gray-600 px-2'
          placeholder='Ask anything...'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          style={{maxHeight: '100px'}}
        />
        <motion.button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          whileTap={{ scale: 0.95 }}
          className='p-2 ml-2 rounded-full bg-blue-600/10 hover:bg-blue-600/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        > 
          <PaperAirplaneIcon className='w-5 h-5 text-blue-400' />
        </motion.button>
      </div>
      
      {/* Resize Handle (Top-Left) */}
      <div
        onMouseDown={startResize}
        className='absolute top-0 left-0 w-6 h-6 cursor-nw-resize
                   z-50 flex items-start justify-start p-1 group'
      >
        <div className="w-2.5 h-2.5 border-t-2 border-l-2 border-gray-600 rounded-tl-sm group-hover:border-blue-400 transition-colors" />
      </div>

    </motion.div>
  )
}